import { useEffect, useRef, useState } from "react";
import { websocketUrl } from "../app/api";
import { DictaLogo } from "./DictaLogo";

interface SharedTranscriptRow {
  id: string;
  speaker: string;
  text: string;
  provisional?: boolean;
}

type SharedState =
  | "connecting"
  | "live"
  | "ended"
  | "expired"
  | "revoked"
  | "unavailable";

interface SharedMessage {
  type: string;
  rows?: SharedTranscriptRow[];
  row?: SharedTranscriptRow;
  provisionalRowId?: string;
  status?: string;
}

export function SharedLiveTranscript({ token }: { token: string }) {
  const [rows, setRows] = useState<SharedTranscriptRow[]>([]);
  const [state, setState] = useState<SharedState>("connecting");
  const terminalState = useRef<SharedState | null>(null);
  const transcript = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Live transcript · Dicta";
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow, noarchive";
    document.head.append(robots);

    let socket: WebSocket | null = null;
    let reconnect: number | null = null;
    let attempts = 0;
    let disposed = false;

    const connect = () => {
      if (disposed || terminalState.current) return;
      setState("connecting");
      socket = new WebSocket(
        websocketUrl(`/api/shared/${encodeURIComponent(token)}/live`)
      );
      socket.addEventListener("open", () => {
        attempts = 0;
      });
      socket.addEventListener("message", (event) => {
        let message: SharedMessage;
        try {
          message = JSON.parse(String(event.data)) as SharedMessage;
        } catch {
          return;
        }
        if (message.type === "share.snapshot") {
          setRows(message.rows ?? []);
          setState(
            message.status === "processing" ||
              message.status === "review" ||
              message.status === "done" ||
              message.status === "interrupted"
              ? "ended"
              : "live"
          );
          return;
        }
        if (
          (message.type === "transcript.delta" ||
            message.type === "transcript.commit") &&
          message.row
        ) {
          const incoming = message.row;
          setRows((current) => {
            const next = current.filter(
              (row) =>
                row.id !== incoming.id &&
                row.id !== message.provisionalRowId
            );
            return [
              ...next,
              {
                ...incoming,
                provisional: message.type === "transcript.delta"
              }
            ];
          });
          setState("live");
          return;
        }
        if (message.type === "share.ended") {
          setState("ended");
          return;
        }
        if (message.type === "share.revoked" || message.type === "share.expired") {
          const next = message.type === "share.revoked" ? "revoked" : "expired";
          terminalState.current = next;
          setRows([]);
          setState(next);
        }
      });
      socket.addEventListener("close", () => {
        if (disposed || terminalState.current) return;
        attempts += 1;
        if (attempts >= 3) {
          setRows([]);
          setState("unavailable");
          return;
        }
        reconnect = window.setTimeout(connect, 1000 * attempts);
      });
    };

    connect();
    return () => {
      disposed = true;
      if (reconnect) window.clearTimeout(reconnect);
      socket?.close();
      document.title = previousTitle;
      robots.remove();
    };
  }, [token]);

  useEffect(() => {
    if (typeof transcript.current?.scrollTo === "function") {
      transcript.current.scrollTo({
        top: transcript.current.scrollHeight,
        behavior: "smooth"
      });
    }
  }, [rows]);

  const terminal = state === "expired" || state === "revoked" || state === "unavailable";
  return (
    <main className="shared-transcript-page">
      <header className="shared-transcript-page__head">
        <DictaLogo />
        <div>
          <h1>Live transcript</h1>
          <span className={`shared-transcript-state shared-transcript-state--${state}`}>
            {state === "live"
              ? "Live"
              : state === "ended"
                ? "Recording ended"
                : state === "connecting"
                  ? "Connecting…"
                  : "Unavailable"}
          </span>
        </div>
      </header>
      <section
        className="shared-transcript-sheet"
        aria-label="Live transcript"
        aria-live="polite"
        ref={transcript}
      >
        {terminal ? (
          <div className="shared-transcript-empty">
            <strong>This live transcript is no longer available.</strong>
            <span>The link may have expired or been stopped by its owner.</span>
          </div>
        ) : rows.length ? (
          rows.map((row) => (
            <article
              className={`shared-transcript-row ${
                row.provisional ? "is-provisional" : ""
              }`}
              key={row.id}
            >
              <strong>{row.speaker || "Unknown speaker"}</strong>
              <p>{row.text}</p>
            </article>
          ))
        ) : (
          <div className="shared-transcript-empty">
            <strong>{state === "connecting" ? "Connecting…" : "Listening for speech…"}</strong>
            <span>The transcript will appear here as the recording continues.</span>
          </div>
        )}
      </section>
    </main>
  );
}
